import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Pencil, Plus, ShieldCheck, UserRoundX, UserRoundCheck } from 'lucide-react';
import type { AgencyRole, AgencyUser } from '@aimmo/contracts';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Input,
  Label,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  toast,
  useConfirm,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { useAbility } from '../lib/abilities.js';
import { formatDateTime } from '../lib/format.js';
import { queryKeys } from '../lib/queries.js';
import { RoleCheckboxes, RoleChips } from '../features/roles.js';

/**
 * Editing a colleague is a deliberate act (U8).
 *
 * The roles used to be checkboxes on the row, so a mis-click changed someone's
 * access with nothing to confirm and nothing to undo. They live behind a button
 * now, together with the name — which is Keycloak's, mirrored into `users.name`
 * so the list shows the change without waiting for their next sign-in.
 */
function EditUserDialog({ user }: { user: AgencyUser }) {
  const [open, setOpen] = React.useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Modifica ${user.name}`}
        onClick={() => setOpen(true)}
      >
        <Pencil className="size-4" />
      </Button>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifica utente</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        {/* The content unmounts when the dialog closes, so every opening
            starts the form from the user as they are now. */}
        <EditUserForm user={user} onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Seeded once, when the dialog opens: the name arrives as one string and the
 * split is a guess, so re-splitting under the cursor — on a refetch of the
 * list, say — would fight whoever is correcting it.
 */
function EditUserForm({ user, onDone }: { user: AgencyUser; onDone: () => void }) {
  const queryClient = useQueryClient();
  const parts = user.name.trim().split(/\s+/);
  const [firstName, setFirstName] = React.useState(parts[0] ?? '');
  const [lastName, setLastName] = React.useState(parts.slice(1).join(' '));
  const [roles, setRoles] = React.useState<AgencyRole[]>(user.roles);

  const save = useMutation({
    mutationFn: async () => {
      assertOk(
        await api.users.setProfile({
          params: { subject: user.subject },
          body: { firstName, lastName },
        }),
      );
      const sameRoles =
        roles.length === user.roles.length && roles.every((role) => user.roles.includes(role));
      if (!sameRoles) {
        assertOk(await api.users.setRoles({ params: { subject: user.subject }, body: { roles } }));
      }
    },
    onSuccess: async () => {
      toast.success('Utente aggiornato.');
      await queryClient.invalidateQueries({ queryKey: queryKeys.agencyUsers });
      onDone();
    },
    onError: () => toast.error('Modifica non riuscita.'),
  });

  return (
    <>
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="edit-first">Nome</Label>
            <Input
              id="edit-first"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="edit-last">Cognome</Label>
            <Input
              id="edit-last"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-1">
          <Label>Ruoli</Label>
          <RoleCheckboxes value={roles} onChange={setRoles} />
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={save.isPending || roles.length === 0}
          onClick={() => save.mutate()}
        >
          Salva
        </Button>
      </DialogFooter>
    </>
  );
}

/** Disabling somebody ends their sessions, so it asks first (U8); re-enabling does not. */
function DeactivateButton({ user, onToggle }: { user: AgencyUser; onToggle: () => void }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={`${user.active ? 'Disattiva' : 'Riattiva'} ${user.name}`}
      onClick={onToggle}
    >
      {user.active ? <UserRoundX className="size-4" /> : <UserRoundCheck className="size-4" />}
    </Button>
  );
}

function InviteDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [roles, setRoles] = React.useState<AgencyRole[]>(['agent']);

  const invite = useMutation({
    mutationFn: async () =>
      assertOk(await api.users.invite({ body: { email, firstName, lastName, roles } })).body,
    onSuccess: async (user) => {
      toast.success(`Invito inviato a ${user.email}.`, {
        description: 'Sceglierà la password dal link ricevuto via email.',
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.agencyUsers });
      setEmail('');
      setFirstName('');
      setLastName('');
      setRoles(['agent']);
      setOpen(false);
    },
    onError: () => toast.error('Invito non riuscito.'),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button type="button" variant="primary" size="sm" onClick={() => setOpen(true)}>
        <Plus />
        Invita
      </Button>

      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Invita una persona</DialogTitle>
          {/* No password field, by design: the invitation is the only place one
              is chosen, and it is chosen by the person it belongs to. */}
          <DialogDescription>
            Riceverà un’email da cui impostare la propria password e verificare l’indirizzo.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1">
              <Label htmlFor="invite-first">Nome</Label>
              <Input
                id="invite-first"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="invite-last">Cognome</Label>
              <Input
                id="invite-last"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </div>
          </div>

          <fieldset className="grid gap-2">
            <legend className="text-label uppercase tracking-[0.08em] text-faint">Ruoli</legend>
            <RoleCheckboxes value={roles} onChange={setRoles} />
            {(roles.includes('admin') || roles.includes('manager')) && (
              <p className="flex items-center gap-2 text-secondary text-muted">
                <ShieldCheck className="size-4" />
                Admin e manager devono configurare un secondo fattore al primo accesso.
              </p>
            )}
          </fieldset>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
            Annulla
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!email || !firstName || !lastName || roles.length === 0 || invite.isPending}
            onClick={() => invite.mutate()}
          >
            Invia invito
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Who can get in, and with what (U7).
 *
 * Everything here goes through the API, which scopes it to this agency's
 * Keycloak group — the service account behind it can see the whole realm, so
 * the boundary is server-side and this screen is only its face.
 */
export function AdministrationPage() {
  const queryClient = useQueryClient();
  const ability = useAbility();
  const canManage = ability.can('manage', 'User');

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: queryKeys.agencyUsers,
    queryFn: async ({ signal }) =>
      assertOk(await api.users.list({ fetchOptions: { signal } })).body,
    enabled: canManage,
  });

  const setActive = useMutation({
    mutationFn: async ({ user, active }: { user: AgencyUser; active: boolean }) =>
      assertOk(await api.users.setActive({ params: { subject: user.subject }, body: { active } }))
        .body,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.agencyUsers });
    },
  });

  // One confirmation for the whole table rather than a dialog mounted per row;
  // a refusal stays in it with the reason instead of closing as if it worked.
  const { confirm, dialog } = useConfirm();
  const toggle = (user: AgencyUser): void => {
    if (!user.active) {
      setActive.mutate(
        { user, active: true },
        { onError: () => toast.error('Operazione non riuscita.') },
      );
      return;
    }
    confirm(
      {
        title: `Disattivare ${user.name}?`,
        description:
          'Perde subito l’accesso: le sessioni aperte vengono chiuse e non potrà più entrare. La sua storia resta — attività, incarichi e immobili continuano a riferirsi a lui. Puoi riattivarlo in seguito.',
        confirmLabel: 'Disattiva',
      },
      'danger',
      async () => {
        await setActive.mutateAsync({ user, active: false });
      },
    );
  };

  const resend = useMutation({
    mutationFn: async (user: AgencyUser) =>
      assertOk(await api.users.resendInvitation({ params: { subject: user.subject }, body: {} }))
        .body,
    onSuccess: () => toast.success('Invito inviato di nuovo.'),
  });

  if (!canManage) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Amministrazione</CardTitle>
          <CardDescription>Serve il ruolo di amministratore.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <section className="grid gap-[var(--card-gap)]">
      <PageHeader
        title="Utenti"
        description="Chi entra in questa agenzia, con quali ruoli."
        actions={<InviteDialog />}
      />

      <Card>
        <CardContent>
          {isPending && <TableSkeleton rows={5} />}
          {isError && <ErrorState title="Elenco non disponibile" onRetry={() => void refetch()} />}

          {data && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ruoli</TableHead>
                  <TableHead>Ultimo accesso</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((user) => (
                  <TableRow key={user.subject}>
                    <TableCell>
                      <span className="flex flex-wrap items-center gap-2">
                        {user.name}
                        {!user.active && <Badge tone="neutral">disattivato</Badge>}
                        {user.pending && <Badge tone="warning">invito in sospeso</Badge>}
                      </span>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <RoleChips roles={user.roles} />
                    </TableCell>
                    <TableCell>
                      {user.lastLoginAt ? formatDateTime(user.lastLoginAt) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {user.pending && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Reinvia invito a ${user.name}`}
                            onClick={() => resend.mutate(user)}
                          >
                            <Mail className="size-4" />
                          </Button>
                        )}
                        <EditUserDialog user={user} />
                        <DeactivateButton user={user} onToggle={() => toggle(user)} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {dialog}
    </section>
  );
}
